import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { PublicCmsPage, PublicCmsBlock } from "./cms-public.functions";

/**
 * Draft preview tokens for CMS pages.
 *
 * - createCmsPreviewToken (auth, editor) mints a token bound to a page id.
 *   Token expires after 7 days (DB default).
 * - getCmsPagePreview (public) accepts a token and returns the *draft*
 *   page + blocks regardless of publish status. Uses supabaseAdmin
 *   loaded inside the handler — the anon SELECT policy on
 *   cms_preview_tokens was removed in a security migration, so we cannot
 *   look up tokens with the publishable key.
 */

async function assertEditor(
  supabase: import("@supabase/supabase-js").SupabaseClient,
  userId: string,
) {
  const roles = ["content_editor", "admin", "super_admin"] as const;
  for (const r of roles) {
    const { data } = await supabase.rpc("has_role", { _user_id: userId, _role: r });
    if (data) return;
  }
  throw new Error("Forbidden: editor role required");
}

export const createCmsPreviewToken = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d) => z.object({ pageId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }): Promise<{ token: string; expires_at: string }> => {
    const { supabase, userId } = context;
    await assertEditor(supabase, userId);
    const { data: row, error } = await supabase
      .from("cms_preview_tokens")
      .insert({
        resource_type: "cms_page",
        resource_id: data.pageId,
        created_by: userId,
      } as never)
      .select("token,expires_at")
      .single();
    if (error) throw new Error(error.message);
    return { token: row.token as string, expires_at: row.expires_at as string };
  });

export const getCmsPagePreview = createServerFn({ method: "GET" })
  .validator((d) =>
    z.object({
      token: z.string().min(16).max(128),
      slug: z.string().min(1).max(400),
    }).parse(d),
  )
  .handler(async ({ data }): Promise<PublicCmsPage | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // 1. Validate token: must exist, must not be expired, must match the slug.
    const { data: tok, error: tErr } = await supabaseAdmin
      .from("cms_preview_tokens")
      .select("resource_type,resource_id,expires_at")
      .eq("token", data.token)
      .maybeSingle();
    if (tErr) throw new Error(tErr.message);
    if (!tok) return null;
    if (tok.resource_type !== "cms_page" && tok.resource_type !== "page") return null;
    if (new Date(tok.expires_at as string) <= new Date()) return null;

    // 2. Read the draft page bypassing status RLS.
    const { data: page, error } = await supabaseAdmin
      .from("cms_pages")
      .select("id,slug,title,meta_title,meta_description,og_image_url,canonical_url,noindex,layout,template,body,published_at,updated_at,status")
      .eq("id", tok.resource_id as string)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!page || page.slug !== data.slug) return null;

    const { data: rawBlocks, error: bErr } = await supabaseAdmin
      .from("cms_page_blocks")
      .select("id,block_type,position,inline_content,device_visibility,block_id,cms_blocks(id,block_key,block_type,content,is_active,archived_at)")
      .eq("page_id", page.id)
      .order("position", { ascending: true });
    if (bErr) throw new Error(bErr.message);

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const blocks: PublicCmsBlock[] = (rawBlocks ?? []).map((b: any) => {
      const g = b.cms_blocks;
      const global = g && g.is_active && !g.archived_at ? {
        id: g.id as string, block_key: g.block_key as string,
        block_type: g.block_type as string, content: (g.content ?? {}) as Record<string, any>,
      } : null;
      return {
        id: b.id, block_type: b.block_type, position: b.position,
        inline_content: (b.inline_content ?? {}) as Record<string, any>,
        device_visibility: b.device_visibility ?? ["desktop","tablet","mobile"],
        global,
      };
    });
    /* eslint-enable @typescript-eslint/no-explicit-any */

    return {
      id: page.id, slug: page.slug, title: page.title,
      meta_title: page.meta_title, meta_description: page.meta_description,
      og_image_url: page.og_image_url, canonical_url: page.canonical_url,
      noindex: true, // previews are never indexed
      layout: page.layout, template: page.template,
      body: (page.body ?? {}) as Record<string, unknown>,
      published_at: page.published_at, updated_at: page.updated_at,
      blocks,
    };
  });

